import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Clock, Tag, ArrowRight, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { fetchDestinations, resolveImageUrl } from '../lib/travelApi';
import DestinationOptionsModal from '../components/DestinationOptionsModal';

const DestinationsPage = () => {
  const navigate = useNavigate();
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [customDestination, setCustomDestination] = useState('');

  useEffect(() => {
    fetchDestinations().then(setDestinations).catch(() => setDestinations([]));
  }, []);

  const handleDestinationClick = (destination) => {
    setSelectedDestination(destination);
  };

  const handleCustomInquiry = (e) => {
    e.preventDefault();
    const value = customDestination.trim();
    if (!value) {
      toast.error('Please type your preferred destination');
      return;
    }
    navigate('/contact', {
      state: { destination: value, inquiryType: 'direct' },
    });
  };

  const handleOptionSelect = (optionId) => {
    if (!selectedDestination) return;

    switch (optionId) {
      case 'inquiry':
        navigate('/contact', { 
          state: { 
            destination: selectedDestination.name,
            inquiryType: 'direct'
          } 
        });
        break;
      case 'trip-builder':
        navigate(`/destination/${selectedDestination.id}/trip-builder`);
        break;
      case 'activities':
        navigate(`/destination/${selectedDestination.id}/activities`);
        break;
      default:
        break;
    }
    setSelectedDestination(null);
  };

  return (
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Popular Destinations
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore our handpicked destinations for unforgettable experiences
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {destinations.map((destination) => (
            <Card
              key={destination.id}
              onClick={() => handleDestinationClick(destination)}
              data-testid={`destination-card-${destination.id}`}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={resolveImageUrl(destination.image)}
                  alt={destination.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-xl font-bold text-white mb-1">
                    {destination.name}
                  </h3>
                </div>
                <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {destination.activities.length} Activities
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {destination.description}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-amber-600">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{destination.duration}</span>
                  </div>
                  <div className="flex items-center text-gray-500">
                    <Tag className="w-4 h-4 mr-1" />
                    <span className="text-xs">{destination.type}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center text-amber-600 font-semibold group-hover:translate-x-1 transition-transform">
                  Plan Your Trip <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Don't see your destination? Custom inquiry */}
        <div className="max-w-3xl mx-auto mt-16">
          <Card className="border-2 border-dashed border-amber-300 bg-amber-50/60" data-testid="custom-destination-card">
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Don&apos;t see your destination?
              </h3>
              <p className="text-gray-600 mb-6">
                Tell us where you&apos;d love to go and we&apos;ll craft a custom trip just for you.
              </p>
              <form onSubmit={handleCustomInquiry} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
                <Input
                  data-testid="custom-destination-input"
                  value={customDestination}
                  onChange={(e) => setCustomDestination(e.target.value)}
                  placeholder="e.g. Switzerland, Maldives, Japan..."
                  className="flex-1 h-12 bg-white"
                />
                <Button
                  type="submit"
                  data-testid="custom-destination-submit"
                  className="h-12 bg-amber-600 hover:bg-amber-700 text-white px-6"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Inquiry
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Options Modal */}
      {selectedDestination && (
        <DestinationOptionsModal
          destination={selectedDestination}
          onClose={() => setSelectedDestination(null)}
          onOptionSelect={handleOptionSelect}
        />
      )}
    </section>
  );
};

export default DestinationsPage;
